/**
 * Gesture guards — Phase 4.7 Pass B, extended by the 4.5 device correction
 *
 * On iPhone, a two-finger pinch was handled by Safari itself: it zoomed the page, and an
 * overscroll pull could reload it — which threw away whatever Arvexis was saying at that moment.
 * The first repair guarded the conversation element only, so the Founder measured the page still
 * zooming when the pinch began on the header, the composer, the whitespace or a control, leaving
 * the whole interface at an inconsistent scale.
 *
 * The boundary is now the whole Message shell: the gesture is refused at the document, in the
 * capture phase, everywhere except inside a dedicated viewer. The page viewport is deliberately
 * left alone (`user-scalable` is NOT disabled, so accessibility enlargement and system text
 * scaling keep working) and no transform or fixed width is used to imitate stability — nothing is
 * scaled, because nothing is allowed to scale.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const twoFingerDistance = (touches) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

// The one place a pinch is a real gesture. Marked by usePinchZoom on the element it drives.
const PINCH_SURFACE = '[data-pinch-surface="true"]';

const insideViewer = (target) =>
  !!(target && typeof target.closest === 'function' && target.closest(PINCH_SURFACE));

/**
 * Refuse page pinch-zoom across the whole Message shell, for as long as it is mounted.
 *
 * One-finger scrolling, taps, text selection and the keyboard are untouched: only Safari's own
 * gesture events and genuinely multi-finger touches are refused, and only outside the viewers.
 */
export const useShellGestureGuard = () => {
  useEffect(() => {
    const blockGesture = (e) => {
      if (insideViewer(e.target)) return;
      if (e.cancelable) e.preventDefault();
    };
    const blockMultiTouch = (e) => {
      if (!e.touches || e.touches.length < 2) return;
      if (insideViewer(e.target)) return;
      if (e.cancelable) e.preventDefault();
    };

    const options = { passive: false, capture: true };
    document.addEventListener('gesturestart', blockGesture, options);
    document.addEventListener('gesturechange', blockGesture, options);
    document.addEventListener('gestureend', blockGesture, options);
    document.addEventListener('touchstart', blockMultiTouch, options);
    document.addEventListener('touchmove', blockMultiTouch, options);

    return () => {
      document.removeEventListener('gesturestart', blockGesture, options);
      document.removeEventListener('gesturechange', blockGesture, options);
      document.removeEventListener('gestureend', blockGesture, options);
      document.removeEventListener('touchstart', blockMultiTouch, options);
      document.removeEventListener('touchmove', blockMultiTouch, options);
    };
  }, []);
};

/**
 * Pinch and pan, inside a viewer only.
 *
 * Listeners are native and non-passive on purpose: React registers touch handlers passively at
 * the root, so `preventDefault()` from a JSX handler is ignored and the gesture would reach the
 * page underneath.
 */
export const usePinchZoom = ({ min = 1, max = 4 } = {}) => {
  // A callback ref, not an object ref: the viewer element only exists while the viewer is open,
  // so an effect keyed on mount alone would attach to nothing and the gesture would fall through
  // to the page. Measured — pinch inside the viewer did nothing at all.
  const [node, setNode] = useState(null);
  const ref = useCallback((el) => setNode(el), []);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const gesture = useRef(null);
  const latest = useRef(view);
  latest.current = view;

  const reset = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);

  const toggleZoom = useCallback(() => {
    setView((v) => (v.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.2, x: 0, y: 0 }));
  }, []);

  useEffect(() => {
    const el = node;
    if (!el) return;

    // The shell guard refuses every pinch it sees; this attribute is how it recognises the one
    // place a pinch is a real gesture, so the viewer keeps working while the page never scales.
    el.setAttribute('data-pinch-surface', 'true');

    const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };

    const onStart = (e) => {
      e.stopPropagation();
      const touches = Array.from(e.touches);
      if (touches.length === 2) {
        gesture.current = { kind: 'pinch', dist: twoFingerDistance(touches), scale: latest.current.scale };
        if (e.cancelable) e.preventDefault();
      } else if (touches.length === 1 && latest.current.scale > 1) {
        gesture.current = {
          kind: 'pan',
          px: touches[0].clientX, py: touches[0].clientY,
          x: latest.current.x, y: latest.current.y,
        };
      } else {
        gesture.current = null;
      }
    };

    const onMove = (e) => {
      const g = gesture.current;
      if (!g) {
        if (e.touches.length > 1 && e.cancelable) swallow(e);
        return;
      }
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      if (g.kind === 'pinch' && e.touches.length === 2) {
        const factor = twoFingerDistance(Array.from(e.touches)) / (g.dist || 1);
        const scale = Math.min(max, Math.max(min, g.scale * factor));
        setView((v) => (scale <= 1.001 ? { scale: 1, x: 0, y: 0 } : { ...v, scale }));
      } else if (g.kind === 'pan' && e.touches.length === 1) {
        setView((v) => ({
          ...v,
          x: g.x + (e.touches[0].clientX - g.px),
          y: g.y + (e.touches[0].clientY - g.py),
        }));
      }
    };

    const onEnd = (e) => {
      e.stopPropagation();
      gesture.current = null;
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: false });
    el.addEventListener('touchcancel', onEnd, { passive: false });
    el.addEventListener('gesturestart', swallow, { passive: false });
    el.addEventListener('gesturechange', swallow, { passive: false });
    el.addEventListener('gestureend', swallow, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('gesturestart', swallow);
      el.removeEventListener('gesturechange', swallow);
      el.removeEventListener('gestureend', swallow);
    };
  }, [node, min, max]);

  return {
    ref,
    view,
    reset,
    toggleZoom,
    style: {
      transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
      transition: gesture.current ? 'none' : 'transform 180ms ease-out',
      touchAction: 'none',
    },
  };
};
